import type { Sku } from '@commercelayer/sdk'
import type { Order } from '@commercelayer/sdk'

export type SizebayOrderBody = Partial<{
  id: string
  currency_code: string
  line_items: Array<
    Partial<{
      id?: string
      sku_code: string
      formatted_unit_amount: string
      quantity: number
      item?: {
        id: string
      }
      metadata?: {
        path: string
        size: string
      }
    }>
  >
}>
/**
 * Composable for Sizebay tracking functionality
 *
 * Provides methods to track e-commerce events for the Sizebay sizing service.
 *
 * @returns {Object} An object containing tracking methods:
 * - trackingAddToCart: Tracks when a product is added to cart
 * - trackingFinalizeOrder: Tracks when an order is finalized
 *
 * @description
 * - Uses the Sizebay API to track e-commerce events
 * - Sends data about products and orders to Sizebay for analytics and size recommendations
 * - Requires a valid Sizebay tenant ID from the runtime config
 *
 * @example
 * // Import the composable
 * const { trackingAddToCart, trackingFinalizeOrder } = await useSizebayTracker()
 *
 * // Track add to cart event
 * await trackingAddToCart(cartItem)
 *
 * // Track order finalization
 * await trackingFinalizeOrder(orderData)
 */
export const useSizebayTracker = async () => {
  const config = useRuntimeConfig()
  const sizeBayId = config.public.sizebay as string
  const baseUrl = window.location?.origin

  const userDeviceMap = {
    m: 'MOBILE',
    t: 'TABLET',
    d: 'DESKTOP',
  }
  const { country } = useRouteHelper()
  const sid = useCookie('SIZEBAY_SESSION_ID_V4').value || ''

  type SizebayLineItem = {
    permalink: string
    price: string
    quantity: number
    size: string
    feedProductId: string
    sku: string
  }

  const trackingAddToCart = async (item?: LineItem_<Sku> | undefined) => {
    if (!sizeBayId || !item?.sku_code) return
    const skuCode = getSkuWithoutSize(item?.sku_code)
    if (!skuCode) return
    const skuCodeDashed = skuCode.replaceAll('_', '-')
    const productUrl = `${baseUrl}/${skuCodeDashed}`
    const payload = {
      products: [
        {
          permalink: productUrl,
        },
      ],
      tenantId: sizeBayId,
    }
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'x-szb-country': country.toUpperCase(),
      'x-szb-device': userDeviceMap[userDevice],
      'x-szb-tenant-id': sizeBayId,
      referer: baseUrl.replace(/^https?:\/\//, ''),
    }
    try {
      const response = await fetch(
        `https://vfr-v3-production.sizebay.technology/plugin/new/cart?sid=${sid}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      )
      if (!response.ok) {
        console.error('Error', response.statusText, response.status)
        throw new Error('Error')
      }
    } catch (error) {
      console.error('Error', { error })
      throw error
    }
  }

  const trackingFinalizeOrder = async (order: Order) => {
    if (!sizeBayId) return
    if (!order.line_items) {
      console.error('Error', 'No line items')
      return
    }
    const giftToRemoveCodeSet = new Set(
      order.available_free_skus?.map(sku => sku.code) || []
    )
    const line_items: SizebayLineItem[] = order.line_items
      .map((item: any) => {
        if (item.sku_code && !giftToRemoveCodeSet.has(item.sku_code)) {
          const skuCode = getSkuWithoutSize(item?.sku_code)
          if (!skuCode) return
          const skuCodeDashed = skuCode.replaceAll('_', '-')
          const productUrl = `${baseUrl}/${skuCodeDashed}`
          const cleanedSize =
            item.metadata?.size?.replace(/\s*\([^)]+\)$/, '') ?? ''
          return {
            permalink: productUrl,
            price: item.unit_amount_float ?? '',
            quantity: item.quantity ?? 0,
            size: cleanedSize,
            feedProductId: item?.item?.id ?? '',
            sku: item.sku_code ?? '',
          }
        }
      })
      .filter((item): item is SizebayLineItem => item !== undefined)

    const payload = {
      orderId: order.number,
      items: line_items,
      tenantId: sizeBayId,
      currency: order.currency_code,
    }
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'x-szb-country': country,
      'x-szb-device': userDeviceMap[userDevice],
      'x-szb-tenant-id': sizeBayId,
      referer: baseUrl.replace(/^https?:\/\//, ''),
    }
    try {
      const response = await fetch(
        `https://vfr-v3-production.sizebay.technology/plugin/new/ordered?sid=${sid}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        }
      )
      if (!response.ok) {
        console.error('Error', response.statusText, response.status)
        throw new Error('Error')
      }
    } catch (error) {
      console.error('Error', { error })
      throw error
    }
  }

  return {
    trackingAddToCart,
    trackingFinalizeOrder,
  }
}
